import React, { useState } from 'react';
import styles from './CalendarWorkoutThumbnail.module.scss';
import classNames from 'classnames';
import PendingOutlinedIcon from '@mui/icons-material/PendingOutlined';
import Menu from '../atoms/Menu';
import { locales } from '../../constants';
import BorderColorOutlinedIcon from '@mui/icons-material/BorderColorOutlined';
import DeleteForeverOutlinedIcon from '@mui/icons-material/DeleteForeverOutlined';
import FitnessCenterIcon from '@mui/icons-material/FitnessCenter';
import WatchLaterIcon from '@mui/icons-material/WatchLater';
import CircleIcon from '@mui/icons-material/Circle';
import { useSessionData } from '../../providers';

import type { TrainingRoutine } from '../../types';

const t = locales.en.components.programs.calendarWorkoutThumbnail;

export interface CalendarWorkoutThumbnailProps {
  routine: TrainingRoutine;
  isSelected: boolean;
  isStartDay: boolean;
  onEditWorkout: () => void;
  onRemoveProgramItem: () => void;
  isDraggable?: boolean;
  isDragging?: boolean;
  onDragStart?: (event: React.DragEvent) => void;
  onDragEnd?: (event: React.DragEvent) => void;
  className?: string;
}

export const CalendarWorkoutThumbnail: React.FC<CalendarWorkoutThumbnailProps> =
  React.memo(
    ({
      routine,
      isSelected,
      isStartDay,
      onEditWorkout,
      onRemoveProgramItem,
      isDraggable = false,
      isDragging = false,
      onDragStart,
      onDragEnd,
      className,
    }) => {
      const { mergedExercisesMap } = useSessionData();

      const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
      const [open, setOpen] = useState(false);
      const menuVisible = Boolean(anchorEl);

      const handleCloseMenuOptions = () => {
        setAnchorEl(null);
        setOpen(false);
      };

      const handleMenuClick = (event: any) => {
        setAnchorEl(event.currentTarget);
        setOpen(true);
      };

      const getMenuItems = () => {
        const menuItems: Array<{
          onClick: () => void;
          label: string;
          icon: React.ReactNode;
        }> = [];
        menuItems.push({
          onClick: () => {
            handleCloseMenuOptions();
            onEditWorkout();
          },
          label: t.editWorkoutDetails,
          icon: <BorderColorOutlinedIcon color="primary" />,
        });

        menuItems.push({
          onClick: () => {
            handleCloseMenuOptions();
            onRemoveProgramItem();
          },
          label: t.deleteWorkout,
          icon: <DeleteForeverOutlinedIcon color="primary" />,
        });

        return menuItems;
      };

      const renderMenu = () => (
        <div className={classNames(styles.fadeInMenu)}>
          <PendingOutlinedIcon
            // @ts-ignore
            onClick={(event) => handleMenuClick(event)}
            className={styles.optionsBtn}
            color="primary"
          />
          <Menu
            anchorEl={anchorEl}
            isOpen={open}
            onClose={handleCloseMenuOptions}
            options={getMenuItems()}
          />
        </div>
      );

      const renderExtraDetails = (groupLength: number) => {
        if (groupLength === 0) return null;
        return (
          <>
            {groupLength === 2 && (
              <div className={styles.text}>{t.superset}</div>
            )}
            {groupLength > 2 && <div className={styles.text}>{t.circuit}</div>}
          </>
        );
      };

      return (
        <div
          className={classNames(
            styles.thumbnail,
            className,
            {
              [styles.selected]: isSelected,
              [styles.startDay]: isStartDay,
              [styles.draggable]: isDraggable,
              [styles.dragging]: isDragging,
            },
          )}
          onMouseEnter={(event) =>
            setAnchorEl(event.currentTarget as HTMLElement)
          }
          onMouseLeave={() => setAnchorEl(null)}
          draggable={isDraggable}
          onDragStart={onDragStart}
          onDragEnd={onDragEnd}
        >
          <div className={styles.section}>
            <div className={styles.name}>{routine.name}</div>
            {menuVisible && renderMenu()}
          </div>
          <div className={styles.section}>
            <div className={styles.content}>
              <FitnessCenterIcon color="primary" className={styles.icon} />
              {routine.exerciseGroups.length} {t.exercises}
            </div>
            {routine.estimatedDuration && (
              <div className={styles.content}>
                <WatchLaterIcon color="primary" className={styles.icon} />
                {routine.estimatedDuration} {t.minutes}
              </div>
            )}
          </div>
          <div className={styles.exerciseGroupsContainer}>
            {routine.exerciseGroups.map((group, index) => {
              if (!group || !group.sets) return null;
              return (
                <div className={styles.groupContainer} key={index}>
                  {renderExtraDetails(group.sets.length)}
                  <div className={styles.exerciseGroup}>
                    {group.sets.map((setGroup, setGroupIndex) => {
                      if (!setGroup || setGroup.length === 0) return null;
                      return (
                        <div
                          key={`exerciseSet-${setGroupIndex}`}
                          className={styles.setGroup}
                        >
                          {setGroupIndex > 0 && <div className={styles.divider} />}
                          <div className={styles.exerciseName}>
                            {(() => {
                              const exerciseId = setGroup[0]?.exerciseId;
                              return exerciseId
                                ? (mergedExercisesMap[exerciseId]?.name ?? group.name)
                                : group.name;
                            })()}
                          </div>
                          <div className={styles.setGroupDetails}>
                            <div className={styles.setGroupDetailsText}>
                              {`${t.sets}: ${setGroup.length}`}
                            </div>
                            <CircleIcon
                              color="primary"
                              style={{
                                fontSize: 6,
                                opacity: 0.6,
                              }}
                            />
                            <div className={styles.setGroupDetailsText}>
                              {`${t.reps}: ${setGroup[0].isAmrap ? 'AMRAP' : setGroup[0].reps}`}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      );
    },
  );
